import React from 'react';
import { LayoutDashboardIcon, UsersIcon, UserIcon, CalendarIcon, BriefcaseIcon, ShieldIcon, StethoscopeIcon, ContactIcon, TrendingUpIcon, SettingsIcon, LogOutIcon, FileTextIcon, ClipboardListIcon, MessageSquareIcon, ChevronsLeftIcon, ChevronsRightIcon, XIcon, BrainIcon, PercentIcon, PackageIcon } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { usePermissions } from '../../hooks/usePermissions';
import { useData } from '../../contexts/DataContext';
import { ModulePermissions } from '../../types';
interface SidebarProps {
  currentModule: string;
  onModuleChange: (module: string) => void;
  isCollapsed: boolean;
  onToggleCollapse: () => void;
  onMobileClose?: () => void;
}
const menuItems = [{
  id: 'dashboard',
  label: 'Panel de Control',
  icon: LayoutDashboardIcon,
  permission: 'dashboard' as keyof ModulePermissions
}, {
  id: 'patients',
  label: 'Pacientes',
  icon: UsersIcon,
  permission: 'patients' as keyof ModulePermissions
}, {
  id: 'doctors',
  label: 'Doctores',
  icon: UserIcon,
  permission: 'doctors' as keyof ModulePermissions
}, {
  id: 'appointments',
  label: 'Citas',
  icon: CalendarIcon,
  permission: 'appointments' as keyof ModulePermissions
}, {
  id: 'clinical-history',
  label: 'Historial Clínico',
  icon: FileTextIcon,
  permission: 'clinicalHistory' as keyof ModulePermissions
}, {
  id: 'services',
  label: 'Servicios',
  icon: BriefcaseIcon,
  permission: 'services' as keyof ModulePermissions
}, {
  id: 'products',
  label: 'Productos',
  icon: PackageIcon,
  permission: 'products' as keyof ModulePermissions
}, {
  id: 'insurance',
  label: 'Seguros',
  icon: ShieldIcon,
  permission: 'insurance' as keyof ModulePermissions
}, {
  id: 'specialties',
  label: 'Especialidades',
  icon: StethoscopeIcon,
  permission: 'specialties' as keyof ModulePermissions
}, {
  id: 'contacts',
  label: 'Contactos',
  icon: ContactIcon,
  permission: 'contacts' as keyof ModulePermissions
}, {
  id: 'opportunities',
  label: 'Oportunidades',
  icon: TrendingUpIcon,
  permission: 'opportunities' as keyof ModulePermissions
}, {
  id: 'follow-up-notes',
  label: 'Notas de Seguimiento',
  icon: MessageSquareIcon,
  permission: 'followUpNotes' as keyof ModulePermissions
}, {
  id: 'audit-log',
  label: 'Bitácora',
  icon: ClipboardListIcon,
  permission: 'auditLog' as keyof ModulePermissions
}, {
  id: 'intelligence',
  label: 'Inteligencia',
  icon: BrainIcon,
  permission: 'intelligence' as keyof ModulePermissions
}, {
  id: 'commissions',
  label: 'Comisiones',
  icon: PercentIcon,
  permission: 'commissions' as keyof ModulePermissions
}, {
  id: 'settings',
  label: 'Configuración',
  icon: SettingsIcon,
  permission: 'settings' as keyof ModulePermissions
}];
export function Sidebar({
  currentModule,
  onModuleChange,
  isCollapsed,
  onToggleCollapse,
  onMobileClose
}: SidebarProps) {
  const {
    logout,
    user
  } = useAuth();
  const {
    canView
  } = usePermissions();
  const {
    isLoading
  } = useData();
  const visibleMenuItems = menuItems.filter(item => {
    if (!user) return false;
    return canView(item.permission);
  });
  return <aside className={`h-screen bg-white dark:bg-gray-800 border-r border-gray-200 dark:border-gray-700 flex flex-col transition-all duration-300 ease-in-out ${isCollapsed ? 'w-[68px]' : 'w-64'}`} data-id="element-165">
      {/* Logo */}
      <div className="p-4 border-b border-gray-200 dark:border-gray-700 flex items-center justify-between min-h-[65px]" data-id="element-166">
        <button onClick={() => onModuleChange('dashboard')} className={`flex items-center gap-3 overflow-hidden hover:opacity-80 transition-opacity ${isCollapsed ? 'justify-center w-full' : ''}`} title="Ir al Panel de Control" data-id="element-167">
          <img src="https://cdn.magicpatterns.com/uploads/a741bfNsYBMGGZNxniTt4s/images.jpg" alt="Savidna Logo" className="w-9 h-9 rounded-full object-cover flex-shrink-0" data-id="element-168" />
          {!isCollapsed && <div className="min-w-0 text-left" data-id="element-169">
              <h1 className="text-lg font-bold text-gray-900 dark:text-white truncate" data-id="element-170">
                Savidna
              </h1>
              <p className="text-xs text-gray-500 dark:text-gray-400 truncate" data-id="element-171">
                CRM Médico
              </p>
            </div>}
        </button>
        {/* Mobile close button */}
        {onMobileClose && <button onClick={onMobileClose} className="md:hidden p-2 text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors flex-shrink-0" aria-label="Cerrar menú" data-id="element-172">
            <XIcon className="w-5 h-5" data-id="element-173" />
          </button>}
      </div>

      {/* Toggle Button - debajo del logo, solo en desktop */}
      <button onClick={onToggleCollapse} className="hidden md:flex ml-3 my-2 items-center justify-center w-8 h-8 rounded-full bg-gray-100 hover:bg-gray-200 dark:bg-gray-700 dark:hover:bg-gray-600 text-gray-500 dark:text-gray-400 transition-colors" title={isCollapsed ? 'Expandir menú' : 'Colapsar menú'} data-id="element-174">
        {isCollapsed ? <ChevronsRightIcon className="w-4 h-4" data-id="element-175" /> : <ChevronsLeftIcon className="w-4 h-4" data-id="element-176" />}
      </button>

      {/* Navigation */}
      <nav className="flex-1 overflow-y-auto py-2" data-id="element-177">
        <ul className="space-y-1 px-2" data-id="element-178">
          {visibleMenuItems.map(item => {
          const Icon = item.icon;
          const isActive = currentModule === item.id;
          return <li key={item.id} data-id="element-179">
                <button onClick={() => onModuleChange(item.id)} title={isCollapsed ? item.label : undefined} className={`
                    w-full flex items-center gap-3 rounded-lg
                    text-sm font-medium transition-colors relative group
                    ${isCollapsed ? 'px-0 py-2.5 justify-center' : 'px-3 py-2.5'}
                    ${isActive ? 'bg-sky-50 text-sky-600 dark:bg-sky-900/20 dark:text-sky-400' : 'text-gray-700 hover:bg-gray-100 dark:text-gray-300 dark:hover:bg-gray-700'}
                  `} data-id="element-180">
                  <Icon className="w-5 h-5 flex-shrink-0" data-id="element-181" />
                  {!isCollapsed && <span className="truncate" data-id="element-182">{item.label}</span>}
                </button>
              </li>;
        })}
        </ul>
      </nav>

      {/* Logout */}
      <div className="p-3 border-t border-gray-200 dark:border-gray-700" data-id="element-183">
        <button onClick={logout} title={isCollapsed ? 'Cerrar Sesión' : undefined} className={`w-full flex items-center gap-3 rounded-lg text-sm font-medium text-red-600 hover:bg-red-50 dark:text-red-400 dark:hover:bg-red-900/20 transition-colors relative group ${isCollapsed ? 'px-0 py-2.5 justify-center' : 'px-3 py-2.5'}`} data-id="element-184">
          <LogOutIcon className="w-5 h-5 flex-shrink-0" data-id="element-185" />
          {!isCollapsed && <span data-id="element-186">Cerrar Sesión</span>}
        </button>
      </div>
    </aside>;
}